import React, { useState, useEffect } from "react";

// Import Firebase config
import { db } from './firebase'; // Adjust path as needed
import { doc, getDoc, updateDoc } from 'firebase/firestore';

function SubscriptionPlan() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state for editing
  const [editForm, setEditForm] = useState({
    cost: '',
    validity_days: ''
  });

  useEffect(() => {
    fetchSubscriptionPlans();
  }, []);

  const fetchSubscriptionPlans = async () => {
    try {
      setLoading(true);
      
      const docRef = doc(db, 'subscription', 'plans');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const plansArray = [];
        
        // Convert the plans object to array
        Object.keys(data).forEach(key => {
          if (key.startsWith('plan_')) {
            plansArray.push({
              id: key,
              ...data[key]
            });
          }
        });
        
        // Sort plans by id for consistent ordering
        plansArray.sort((a, b) => a.id.localeCompare(b.id));
        setPlans(plansArray);
      } else {
        setError('No subscription plans found');
      }
      
    } catch (err) {
      console.error('Error fetching subscription plans:', err);
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan.id);
    setEditForm({
      cost: plan.cost.toString(),
      validity_days: plan.validity_days.toString()
    });
  };

  const handleSave = async (planId) => {
    if (!editForm.cost || !editForm.validity_days) {
      alert('Please fill all fields');
      return;
    }

    if (isNaN(editForm.cost) || isNaN(editForm.validity_days) || 
        Number(editForm.cost) <= 0 || Number(editForm.validity_days) <= 0) {
      alert('Please enter valid positive numbers');
      return;
    }

    try {
      setSaving(true);
      
      // Update in Firestore
      const docRef = doc(db, 'subscription', 'plans');
      const updateData = {
        [`${planId}.cost`]: Number(editForm.cost),
        [`${planId}.validity_days`]: Number(editForm.validity_days)
      };
      
      await updateDoc(docRef, updateData);
      
      // Update local state
      setPlans(prevPlans => 
        prevPlans.map(plan => 
          plan.id === planId 
            ? {
                ...plan,
                cost: Number(editForm.cost),
                validity_days: Number(editForm.validity_days)
              }
            : plan
        )
      );
      
      setEditingPlan(null);
      setEditForm({ cost: '', validity_days: '' });
      alert('Plan updated successfully!');
      
    } catch (err) {
      console.error('Error updating plan:', err);
      alert('Failed to update plan. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingPlan(null);
    setEditForm({ cost: '', validity_days: '' });
  };

//   const getPlanIcon = (planName) => {
//     if (planName.toLowerCase().includes('plus')) return 'fas fa-plus';
//     if (planName.toLowerCase().includes('premium')) return 'fas fa-star';
//     if (planName.toLowerCase().includes('gold')) return 'fas fa-crown';
//     return 'fas fa-heart';
//   };

//   const getPlanColor = (planName) => {
//     if (planName.toLowerCase().includes('plus')) return '#4a90e2';
//     if (planName.toLowerCase().includes('premium')) return '#9b59b6';
//     if (planName.toLowerCase().includes('gold')) return '#f39c12';
//     return '#e74c3c';
//   };

  const formatValidityDays = (days) => {
    if (days >= 365) return `${Math.floor(days / 365)} year${Math.floor(days / 365) > 1 ? 's' : ''}`;
    if (days >= 30) return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? 's' : ''}`;
    return `${days} days`;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#4a90e2' }}></i>
          <p>Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '2rem', color: '#e74c3c' }}></i>
          <p>{error}</p>
          <button onClick={fetchSubscriptionPlans} style={styles.retryButton}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
   
    <div style={styles.container}>
      <h2 style={styles.heading}>
        <i className="fas fa-crown text-yellow-500 text-2xl" style={{ marginRight: "10px", color: "#FF027D;" }}></i>
        Subscription Plans
      </h2>

      <div style={styles.cardContainer}>
        {plans.map((plan) => (
          <div key={plan.id} style={styles.card}>
            <div style={styles.cardHeader}>
              <i 
                // className={getPlanIcon(plan.name)} 
                className={(plan.name)}
                style={{
                  ...styles.icon,
                //   color: getPlanColor(plan.name)
                color: (plan.name)
                }}
              ></i>
              {/* <span style={styles.planId}>{plan.id}</span> */}
            </div>

            <h3 style={styles.planName}>{plan.name}</h3>

            {editingPlan === plan.id ? (
              // Edit Mode
              <div style={styles.editSection}>
                <div style={styles.editField}>
                  <label style={styles.label}>Cost (in pounds):</label>
                  <input
                    type="number"
                    value={editForm.cost}
                    onChange={(e) => setEditForm({...editForm, cost: e.target.value})}
                    style={styles.input}
                    min="1"
                    disabled={saving}
                  />
                </div>
                
                <div style={styles.editField}>
                  <label style={styles.label}>Validity (Days):</label>
                  <input
                    type="number"
                    value={editForm.validity_days}
                    onChange={(e) => setEditForm({...editForm, validity_days: e.target.value})}
                    style={styles.input}
                    min="1"
                    disabled={saving}
                  />
                </div>

                <div style={styles.editActions}>
                  <button 
                    onClick={() => handleSave(plan.id)}
                    style={styles.saveButton}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <i className="fas fa-spinner fa-spin" style={{ marginRight: '5px' }}></i>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check" style={{ marginRight: '5px' }}></i>
                        Save
                      </>
                    )}
                  </button>
                  <button 
                    onClick={handleCancel}
                    style={styles.cancelButton}
                    disabled={saving}
                  >
                    <i className="fas fa-times" style={{ marginRight: '5px' }}></i>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div style={styles.viewSection}>
                <p style={styles.planDescription}>
                  {/* Valid for {formatValidityDays(plan.validity_days)} */}
                  Valid for {(plan.validity_days)} days

                </p>
                <p style={styles.planPrice}>
                  <strong>£ {plan.cost}</strong>
                </p>
                
                <div style={styles.planActions}>
                  {/* <button 
                    style={{
                      ...styles.subscribeButton,
                      backgroundColor: getPlanColor(plan.name)
                    }}
                    onClick={() => handleSubscribe(plan)}
                  >
                    <i className="fas fa-shopping-cart" style={{ marginRight: '8px' }}></i>
                    Choose Plan
                  </button> */}
                  
                  <button 
                    onClick={() => handleEdit(plan)}
                    style={styles.editButton}
                    disabled={editingPlan !== null}
                  >
                    <i className="fas fa-edit" style={{ marginRight: '5px' }}></i>
                    Edit
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {plans.length === 0 && (
        <div style={styles.noPlans}>
          <i className="fas fa-info-circle" style={{ fontSize: '2rem', color: '#3498db' }}></i>
          <p>No subscription plans available at the moment.</p>
        </div>
      )}
    </div>
  
  );
}

const handleSubscribe = (plan) => {
  // Handle subscription logic here
  console.log('Selected plan:', plan);
  alert(`You selected ${plan.name} plan for in pounds${plan.cost}`);
};

const styles = {
  container: {
    padding: "40px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    textAlign: "center",
    // backgroundColor: "#f8f9fa",
    minHeight: "100vh",
  },
 
  heading: {
    fontSize: "2.5rem",
    marginBottom: "40px",
    color: "#ee4782;",
    fontWeight: "600",
  },
  cardContainer: {
    display: "flex",
    gap: "30px",
    justifyContent: "center",
    flexWrap: "wrap",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  card: {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "25px 20px",
    width: "300px",
    boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
    transition: "all 0.3s ease",
    border: "2px solid transparent",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
  },
  icon: {
    fontSize: "2.5rem",
  },
  planId: {
    fontSize: "0.8rem",
    color: "#7f8c8d",
    fontWeight: "600",
    backgroundColor: "#ecf0f1",
    padding: "4px 10px",
    borderRadius: "12px",
  },
  planName: {
    fontSize: "1.5rem",
    margin: "10px 0 20px 0",
    color: "#2c3e50",
    fontWeight: "600",
  },
  viewSection: {
    display: "flex",
    flexDirection: "column",
    gap: "15px",
  },
  editSection: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    textAlign: "left",
    backgroundColor: "#f8f9fc",
    padding: "20px",
    borderRadius: "12px",
    border: "2px solid #e3f2fd",
  },
  editField: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  label: {
    fontSize: "0.9rem",
    fontWeight: "600",
    color: "#34495e",
  },
  input: {
    padding: "12px",
    border: "2px solid #e1e8ed",
    borderRadius: "8px",
    fontSize: "1rem",
    transition: "border-color 0.3s ease",
    backgroundColor: "white",
  },
  editActions: {
    display: "flex",
    gap: "12px",
    justifyContent: "center",
    marginTop: "10px",
  },
  planDescription: {
    color: "#7f8c8d",
    fontSize: "1rem",
    margin: "0",
  },
  planPrice: {
    fontSize: "2rem",
    margin: "0",
    color: "#2c3e50",
  },
  planActions: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    marginTop: "10px",
  },
  subscribeButton: {
    backgroundColor: "#e74c3c",
    color: "white",
    border: "none",
    borderRadius: "25px",
    padding: "12px 25px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  editButton: {
    backgroundColor: "#ee4782",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "0.9rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
  },
  saveButton: {
    backgroundColor: "#27ae60",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 8px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    flex: 1,
  },
  cancelButton: {
    backgroundColor: "#95a5a6",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "12px 20px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    flex: 1,
  },
  loading: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
    gap: "20px",
  },
  error: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
    gap: "20px",
    color: "#e74c3c",
  },
  noPlans: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "200px",
    gap: "20px",
    color: "#3498db",
  },
  retryButton: {
    backgroundColor: "#3498db",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "10px 20px",
    cursor: "pointer",
    fontSize: "1rem",
  },
};

export default SubscriptionPlan;